import React from 'react';
import moment from 'moment-timezone';
import _capitalize from 'lodash/capitalize';

import { TrackingEvent as TrackingEventType } from '../apiTypes/api';
import { useTrackingData } from './DataProviders';


export default function TrackingEvents({ eventClassName, timestampFormat, eachEvent, emptyMsgClassName }:{
  eventClassName?: string,
  timestampFormat: string,
  eachEvent: ({ content, key, event, index }:{
    content: any,
    key: string | number | undefined,
    event: TrackingEventType,
    index: number
  }) => React.ReactNode,
  emptyMsgClassName?: string
}) {
  const { events = [] } = useTrackingData();

  if( !events.length ) {
    return <div className={emptyMsgClassName}>
      Your label has been created. Tracking status will appear once the carrier has received the shipment.
    </div>;
  }

  if( eachEvent ) {
    return <>
      { events.map( (event, index) => eachEvent({
        content: <TrackingEvent
                   event={event}
                   key={event.occurred_at || index}
                   className={eventClassName}
                   timestampFormat={timestampFormat}
                   index={index}
                 />,
        key: event.occurred_at || index,
        event: event,
        index: index
      }))}
    </>;
  }

  return <>
    { events.map( (event, index) => (
      <TrackingEvent
        event={event}
        key={event.occurred_at || index}
        className={eventClassName}
        timestampFormat={timestampFormat}
        index={index}
      />
    ))}
  </>
}


// See https://momentjs.com/docs/#/displaying/format/ for `timestampFormat`
export function TrackingEvent({ event, className='', timestampFormat='', index }:{
  event: TrackingEventType,
  className?: string,
  timestampFormat?: string,
  index: number
}) {
  const {
    occurred_at: occurredAt,
    city_locality: cityLocality,
    country_code: countryCode,
    state_province: state,
    description,
  } = event;

  // The timestamp we get from ShipEngine has timezone info attached to it (`Z`) but it is not
  // always true that the timestamp is UTC (might actually be from the timezone of the event's location
  // but they always add the `Z`). So we parse w/ `parseZone` to preserve the time (and not try to do
  // any conversion) so we can just show whatever the value is to the user.
  // See https://shipstation.atlassian.net/browse/ENGINE-2712
  const timestamp = occurredAt && moment(occurredAt).isValid() && moment.parseZone(occurredAt);
  const formattedTimestamp = timestamp && timestamp.format(timestampFormat);

  const city = _capitalize((cityLocality || '').toLocaleLowerCase());
  const country = countryCode !== 'US' ? countryCode : '';
  const location = [city, state, country].filter((e) => e).join(', ');

  return (
    <div className={className} role="listitem" data-testid={index === 0 && 'latest-tracking-event'}>
      <span className="timestamp" title={occurredAt || ''}>{formattedTimestamp}</span>
      <span className="description">{description}</span>
      <span className="location">{location}</span>
    </div>
  );
}
